/**
 * Folder-sharing actions on the tourCollaborators GCF.
 * Mirrors the per-tour collaborator services. Owner-only mutations are
 * enforced server-side; admin allowlist can override.
 */

import { callCollaboratorsApi } from './collaboratorsApi';

export interface FolderCollaboratorInfo {
  uid: string;
  email: string | null;
  displayName: string | null;
}

interface ShareFolderResult {
  success: boolean;
  collaborator: FolderCollaboratorInfo;
}

interface ListFolderCollaboratorsResult {
  folderId: string;
  ownerUid: string;
  owner: FolderCollaboratorInfo | null;
  collaborators: FolderCollaboratorInfo[];
}

export async function shareFolder(
  folderId: string,
  email: string,
  ownerUid?: string,
): Promise<ShareFolderResult> {
  return callCollaboratorsApi<ShareFolderResult>({
    action: 'shareFolder',
    folderId,
    email,
    ...(ownerUid ? { ownerUid } : {}),
  });
}

export async function unshareFolder(
  folderId: string,
  collaboratorUid: string,
  ownerUid?: string,
): Promise<{ success: boolean }> {
  return callCollaboratorsApi<{ success: boolean }>({
    action: 'unshareFolder',
    folderId,
    collaboratorUid,
    ...(ownerUid ? { ownerUid } : {}),
  });
}

export async function getFolderCollaborators(
  folderId: string,
  ownerUid?: string,
): Promise<ListFolderCollaboratorsResult> {
  return callCollaboratorsApi<ListFolderCollaboratorsResult>({
    action: 'listFolderCollaborators',
    folderId,
    ...(ownerUid ? { ownerUid } : {}),
  });
}

interface SyncFolderTourCollaboratorsResult {
  success: boolean;
  updatedTourCount: number;
  cascadedUids?: string[];
}

/**
 * Cascade the folder's collaborators onto the underlying tours.
 * - Pass no tourIds to cascade across all tours in the folder
 *   (right after shareFolder).
 * - Pass `tourIds: [tourId]` to cascade just one tour
 *   (right after moveTour into a shared folder).
 */
export async function syncFolderTourCollaborators(
  folderId: string,
  ownerUid?: string,
  tourIds?: string[],
): Promise<SyncFolderTourCollaboratorsResult> {
  return callCollaboratorsApi<SyncFolderTourCollaboratorsResult>({
    action: 'syncFolderTourCollaborators',
    folderId,
    ...(ownerUid ? { ownerUid } : {}),
    ...(tourIds ? { tourIds } : {}),
  });
}
